// Caches intrinsic image dimensions (natural width/height) keyed by image URL.
//
// Why: vignette/exhibit images carry no dimensions anywhere in the system
// (not on the Image model, not in the authored <img> markup), so the browser
// can't reserve space for them before they load. The image therefore expands
// from 0 to its natural height on load and pushes the content below it down —
// the main source of layout shift (CLS) on the qbank study/review screens.
//
// Once an image has loaded we know its natural dimensions, so we remember them
// (in memory + localStorage). The next time that image is rendered — a later
// question in the same block, the review screen, or the same spaced-repetition
// question on a future day — we can stamp width/height attributes on the <img>
// up front and the browser reserves the correct space, eliminating the shift.
//
// This only ever reserves space we have measured, so it cannot itself introduce
// a shift. The first view of a never-before-seen image is unchanged.

export interface ImageDimensions {
  width: number;
  height: number;
}

const STORAGE_KEY = 'ora-image-dimension-cache-v1';
// Keep the persisted cache bounded so it can't grow without limit. Entries are
// tiny; this is plenty for a study session's worth of recurring content.
const MAX_ENTRIES = 500;

type CacheRecord = Record<string, ImageDimensions>;

let memoryCache: Map<string, ImageDimensions> | null = null;

const isUsableDimension = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value > 0;

const loadFromStorage = (): Map<string, ImageDimensions> => {
  const cache = new Map<string, ImageDimensions>();

  if (typeof window === 'undefined' || !window.localStorage) {
    return cache;
  }

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return cache;

    const parsed = JSON.parse(raw) as CacheRecord;
    if (parsed && typeof parsed === 'object') {
      for (const [src, dims] of Object.entries(parsed)) {
        if (isUsableDimension(dims?.width) && isUsableDimension(dims?.height)) {
          cache.set(src, { width: dims.width, height: dims.height });
        }
      }
    }
  } catch {
    // Corrupt or unavailable storage — start empty rather than throw.
  }

  return cache;
};

const getMemoryCache = (): Map<string, ImageDimensions> => {
  if (!memoryCache) {
    memoryCache = loadFromStorage();
  }
  return memoryCache;
};

const persist = (cache: Map<string, ImageDimensions>): void => {
  if (typeof window === 'undefined' || !window.localStorage) return;

  try {
    const record: CacheRecord = {};
    for (const [src, dims] of cache) {
      record[src] = dims;
    }
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch {
    // Quota or serialization failure — the in-memory cache still works for
    // this session, so degrade silently.
  }
};

export const getCachedImageDimensions = (
  src: string | undefined
): ImageDimensions | undefined => {
  if (!src) return undefined;
  return getMemoryCache().get(src);
};

export const setCachedImageDimensions = (
  src: string | undefined,
  width: number,
  height: number
): void => {
  if (!src || !isUsableDimension(width) || !isUsableDimension(height)) return;

  const cache = getMemoryCache();
  const existing = cache.get(src);
  if (existing && existing.width === width && existing.height === height) {
    return;
  }

  // Refresh insertion order (Map preserves it) so eviction drops the
  // least-recently-written entry.
  cache.delete(src);
  cache.set(src, { width, height });

  while (cache.size > MAX_ENTRIES) {
    const oldestKey = cache.keys().next().value;
    if (oldestKey === undefined) break;
    cache.delete(oldestKey);
  }

  persist(cache);
};

// Minimal shape of a content image link carrying intrinsic dimensions. Kept
// structural so it works for vignette and flashcard image links alike.
interface SeedableImageLink {
  image?: {
    src?: string | null;
    annotatedSrc?: string | null;
    width?: number | null;
    height?: number | null;
  } | null;
}

// Warm the cache from image links that already carry intrinsic dimensions
// (populated by the backend). This is what makes space reservation work on the
// *first* load of an image: the dimensions arrive with the content payload, so
// they're cached before the image renders. Both the primary and annotated src
// are seeded since review mode swaps to the annotated image.
export const seedImageDimensionsFromImageLinks = (
  imageLinks:
    ReadonlyArray<SeedableImageLink | null | undefined> | null | undefined
): void => {
  if (!imageLinks) return;

  for (const link of imageLinks) {
    const image = link?.image;
    if (!image?.width || !image?.height) continue;

    if (image.src) {
      setCachedImageDimensions(image.src, image.width, image.height);
    }
    if (image.annotatedSrc) {
      setCachedImageDimensions(image.annotatedSrc, image.width, image.height);
    }
  }
};
